/**
 * Dynamic table width calculator for imported tables
 * Calculates optimal table width based on column count to prevent overflow
 * while maintaining appropriate margins
 */

// Constants for width calculation
export const MIN_TABLE_WIDTH_RATIO = 0.5; // 50% of page width
export const MAX_TABLE_WIDTH_RATIO = 0.95; // 95% of page width
export const MIN_MARGIN = 20; // Minimum margin in pixels on each side

/**
 * Calculates dynamic table width based on column count
 * Uses a tiered scaling approach:
 * - 1-2 columns: 50% width (minimum)
 * - 3-6 columns: scales from 50% to 75%
 * - 7-10 columns: scales from 75% to 90%
 * - 11+ columns: 95% width (maximum)
 *
 * @param {number} columnCount - Number of columns in the table
 * @param {number} pageWidth - Width of the page/design
 * @param {Object} options - Optional configuration
 * @param {number} options.minRatio - Minimum width ratio (default: 0.5)
 * @param {number} options.maxRatio - Maximum width ratio (default: 0.95)
 * @param {number} options.minMargin - Minimum margin in pixels (default: 20)
 * @returns {number} - Calculated table width in pixels
 */
export const calculateDynamicTableWidth = (
  columnCount,
  pageWidth,
  options = {}
) => {
  const {
    minRatio = MIN_TABLE_WIDTH_RATIO,
    maxRatio = MAX_TABLE_WIDTH_RATIO,
    minMargin = MIN_MARGIN
  } = options;

  // Validate inputs
  if (typeof columnCount !== "number" || columnCount < 0) {
    throw new Error("Column count must be a non-negative number");
  }

  if (typeof pageWidth !== "number" || pageWidth <= 0) {
    throw new Error("Page width must be a positive number");
  }

  // Handle edge case of 0 columns (use minimum width)
  if (columnCount === 0) {
    return pageWidth * minRatio;
  }

  let widthRatio;

  if (columnCount <= 2) {
    // Tier 1: Minimum width for 1-2 columns
    widthRatio = minRatio;
  } else if (columnCount <= 6) {
    // Tier 2: Scale from 50% to 75% for 3-6 columns
    // Linear interpolation: 50% at col 3, 75% at col 6
    const tierMin = 0.5;
    const tierMax = 0.75;
    const progress = (columnCount - 3) / (6 - 3); // 0 to 1
    widthRatio = tierMin + progress * (tierMax - tierMin);
  } else if (columnCount <= 10) {
    // Tier 3: Scale from 75% to 90% for 7-10 columns
    // Linear interpolation: 75% at col 7, 90% at col 10
    const tierMin = 0.75;
    const tierMax = 0.9;
    const progress = (columnCount - 7) / (10 - 7); // 0 to 1
    widthRatio = tierMin + progress * (tierMax - tierMin);
  } else {
    // Tier 4: Maximum width for 11+ columns
    widthRatio = maxRatio;
  }

  // Calculate width from ratio
  let calculatedWidth = pageWidth * widthRatio;

  // Enforce margin constraints
  // Table should never exceed (pageWidth - 2 * minMargin)
  const maxAllowedWidth = pageWidth - 2 * minMargin;
  if (calculatedWidth > maxAllowedWidth) {
    calculatedWidth = maxAllowedWidth;
  }

  // Ensure minimum width is also respecting margins
  // Don't enforce minWidth if it would violate margin constraints
  const minWidth = Math.min(pageWidth * minRatio, maxAllowedWidth);
  if (calculatedWidth < minWidth) {
    calculatedWidth = minWidth;
  }

  return calculatedWidth;
};

const tableWidthCalculator = {
  calculateDynamicTableWidth,
  MIN_TABLE_WIDTH_RATIO,
  MAX_TABLE_WIDTH_RATIO,
  MIN_MARGIN
};

export default tableWidthCalculator;
